// [2026-09-05] ADDED — an in-app confirmation dialog, replacing window.confirm().
//
// WHY REPLACE IT AT ALL. `window.confirm` is drawn by the BROWSER, not by us: it says
// "localhost:5176 says", it cannot be styled, it cannot show a formatted consequence, and on this
// product it was the last thing a customer saw before revoking a live credential or removing a
// colleague's access to candidate recordings. A dialog that looks like a browser error is the wrong
// frame for a decision that matters.
//
// It is also SYNCHRONOUS AND BLOCKING — it freezes the event loop, stops React rendering, and in a
// sandboxed or embedded frame it can be suppressed entirely, in which case `window.confirm` returns
// FALSE and the action silently never happens. That failure looks like a broken button.
//
// THE API IS A PROMISE, on purpose. Every call site already lived inside an async function and read
// `if (!window.confirm(...)) return`, so `if (!(await confirm({...}))) return` is a one-line change
// with the same control flow — no callback pyramid, no restructuring of the mutation that follows.
//
// SCOPE: this is for the CUSTOMER app only. src/views/Keys.jsx and src/views/CandidateReport.jsx
// also call window.confirm, but those screens are rendered by proctoring-frontend-admin's shell,
// which does not import platform-theme.css — this dialog would render there completely unstyled.
// Converting those two needs an equivalent built on ui.jsx's shared Modal instead.

import { useCallback, useEffect, useRef, useState } from 'react'
import { PfModal } from './ui.jsx'

/** Returns `[confirm, dialog]`.
 *
 *  `confirm(options)` resolves true if the person confirmed, false if they cancelled, dismissed with
 *  Escape, or clicked the backdrop. Render `dialog` anywhere in the component's tree.
 *
 *  options: { title, message, confirmLabel, cancelLabel, tone: 'danger' | 'primary' }
 */
export function useConfirm() {
  const [pending, setPending] = useState(null)
  // Held in a ref as well as state so the unmount cleanup can settle a promise that is still open
  // without re-running on every render.
  const pendingRef = useRef(null)

  const confirm = useCallback((options) => new Promise((resolve) => {
    const entry = { ...options, resolve }
    pendingRef.current = entry
    setPending(entry)
  }), [])

  // A dialog open when the screen unmounts (a 401 drops the shell, someone navigates away) would
  // otherwise leave its promise forever unsettled, and the `await` after it never returns — the
  // mutation would hang rather than being cancelled. Resolving false is the safe answer: it means
  // "not confirmed", so nothing destructive proceeds.
  useEffect(() => () => {
    if (pendingRef.current) {
      pendingRef.current.resolve(false)
      pendingRef.current = null
    }
  }, [])

  const settle = useCallback((result) => {
    const entry = pendingRef.current
    pendingRef.current = null
    setPending(null)
    entry?.resolve(result)
  }, [])

  const dialog = pending ? (
    <ConfirmDialog options={pending} onSettle={settle} />
  ) : null

  return [confirm, dialog]
}

function ConfirmDialog({ options, onSettle }) {
  const cancelRef = useRef(null)

  // FOCUS LANDS ON CANCEL, NOT CONFIRM. Every use of this dialog in this app is destructive —
  // revoking a credential, deleting a key, removing someone's access. Focusing the confirming button
  // means a stray Enter (from the keystroke that opened the dialog) carries the action out. The safe
  // option is the one under the finger.
  useEffect(() => { cancelRef.current?.focus() }, [])

  const danger = options.tone !== 'primary'

  return (
    <PfModal title={options.title}
             subtitle={options.subtitle}
             onClose={() => onSettle(false)}>
      {/* The consequence, as a block rather than a sentence appended to the title. Callers pass a
          string or JSX; `white-space: pre-line` means an existing message with \n\n in it — which is
          how the window.confirm strings were written — still paragraphs correctly. */}
      <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--pf-ink-2)',
                    whiteSpace: 'pre-line' }}>
        {options.message}
      </div>

      <div className="pf-modal-foot">
        <button type="button" ref={cancelRef} className="pf-btn ghost"
                onClick={() => onSettle(false)}>
          {options.cancelLabel || 'Cancel'}
        </button>
        <button type="button" className={`pf-btn${danger ? ' danger' : ''}`}
                onClick={() => onSettle(true)}>
          {options.confirmLabel || 'Confirm'}
        </button>
      </div>
    </PfModal>
  )
}
